import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  Outlet,
  useNavigate,
} from "react-router-dom";
import React, { Suspense, lazy, useEffect } from "react";
import { useAuth } from "@/hooks/auth";

// Create a simple error boundary component
interface ErrorBoundaryProps {
  children?: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Error caught in ErrorBoundary:", error, errorInfo);

    // You could send this error to an error reporting service
    // Example: sendToErrorReportingService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      console.log("Rendering error boundary fallback", this.state.error);

      // If we're in development, also show error details
      if (process.env.NODE_ENV !== "production" && this.state.error) {
        const errorDetails = (
          <div className="mt-6 p-4 bg-black/50 text-left rounded-lg text-sm overflow-auto max-h-60">
            <p className="font-bold text-red-400">
              Error: {this.state.error.message}
            </p>
            <p className="mt-2 text-gray-400 whitespace-pre-wrap">
              {this.state.error.stack}
            </p>
          </div>
        );

        // If fallback is provided and we're in development, add error details
        if (this.props.fallback && React.isValidElement(this.props.fallback)) {
          // Type assertion to properly type the fallback element
          const fallbackElement = this.props.fallback as React.ReactElement<{
            children?: React.ReactNode;
          }>;

          // Properly add keys to all child elements
          const childrenWithKeys = React.Children.toArray(
            fallbackElement.props.children
          );

          // Add the error details with its own key if in development
          const allChildren =
            process.env.NODE_ENV !== "production"
              ? [
                  ...childrenWithKeys,
                  React.cloneElement(errorDetails, { key: "error-details" }),
                ]
              : childrenWithKeys;

          const fallbackWithDetails = React.cloneElement(
            fallbackElement,
            {},
            allChildren
          );
          return fallbackWithDetails;
        }

        // Default error view with details in development
        return (
          this.props.fallback || (
            <div className="p-8 bg-red-600 text-white">
              <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
              <p>Please try refreshing the page.</p>
              {errorDetails}
            </div>
          )
        );
      }

      // Standard fallback in production
      return (
        this.props.fallback || (
          <div className="p-8 bg-red-600 text-white">
            <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
            <p>Please try refreshing the page.</p>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

// Component to redirect users based on authentication status
const RootRedirect = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading) {
      if (user) {
        // User is authenticated, redirect based on role
        const userRole = user.role || "user";
        if (userRole === "admin") {
          navigate("/admin/home", { replace: true });
        } else {
          navigate("/user/homepage", { replace: true });
        }
      } else {
        // User is not authenticated, redirect to login
        navigate("/auth/login", { replace: true });
      }
    }
  }, [user, isLoading, navigate]);

  // Show loading while checking authentication
  return isLoading ? <Loading /> : null;
};
// import AppLayout from "@/layouts/AppLayout"; // No longer needed
import AuthLayout from "@/layouts/AuthLayout";
import Loading from "@/components/Loading";
import { ProtectedRoute } from "./ProtectedRoute";
import UserWidgetsDashboard from "@/pages/user/WidgetsDashboard";
import EnrolledCourseView from "@/pages/EnrolledCourseView";
import GlobalLayout from "@/layouts/GlobalLayout";
import LessonLayout from "@/layouts/LessonLayout";

// Lazy loaded components
// Commented out as it's currently unused
// const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Courses = lazy(() => import("@/pages/Courses"));
const CourseDetails = lazy(() => import("@/pages/CourseDetails"));
const ApiTestPage = lazy(() => import("@/pages/ApiTestPage"));

// User pages
const UserDashboard = lazy(() => import("@/pages/user/Dashboard"));
const UserCourses = lazy(() => import("@/pages/user/courses"));
const UserCourseDetail = lazy(
  () => import("@/pages/user/courses/CourseDetail")
);
const CourseLesson = lazy(() => import("@/pages/CourseLesson/index"));
const UserProfile = lazy(() => import("@/pages/user/profile"));
const UserProfileEdit = lazy(() => import("@/pages/user/profile/edit"));
const UserCertificates = lazy(() => import("@/pages/user/certificates"));
const HospitalInfoPage = lazy(() => import("@/pages/user/HospitalInfo"));
const UserNotifications = lazy(() => import("@/pages/user/notifications"));
const UserAppleStyleIcons = lazy(() => import("@/pages/user/AppleStyleIcons"));
// Messaging components have been disabled
// const UserMessaging = lazy(() => import("@/pages/user/messaging/AppleMessaging"));
// const AdminMessaging = lazy(() => import("@/pages/admin/Messaging"));

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminHomePage = lazy(() => import("@/pages/admin/HomePage"));
const AdminCourses = lazy(() => import("@/pages/admin/Courses"));
const AdminNewCourse = lazy(() => import("@/pages/admin/NewCourse/index"));
const AdminCourseDetails = lazy(() => import("@/pages/admin/CourseDetails"));
const AdminCourseSections = lazy(() => import("@/pages/admin/CourseSections"));
const AdminSections = lazy(() => import("@/pages/admin/Sections"));
const AdminSectionLessons = lazy(
  () => import("@/pages/admin/Sections/lessons")
);
const EditCourse = lazy(() => import("@/pages/admin/EditCourse"));
const AdminTables = lazy(() => import("@/pages/admin/Tables"));
const AdminTranslations = lazy(() => import("@/pages/admin/Translations"));
const AdminUserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const AdminAuth = lazy(() => import("@/pages/admin/UserManagement/AdminAuth"));
const AdminAuthRequests = lazy(
  () => import("@/pages/admin/UserManagement/AuthRequests")
);
const AdminDepartments = lazy(() => import("@/pages/admin/Departments"));
const AdminComplaints = lazy(() => import("@/pages/admin/Complaints"));
const AdminMediaLibrary = lazy(() => import("@/pages/admin/MediaLibrary"));
const AdminProfilePreview = lazy(() => import("@/pages/admin/profilePreview"));
const AdminProfileEdit = lazy(() => import("@/pages/admin/profileEdit"));
const HospitalInfoEdit = lazy(() => import("@/pages/admin/HospitalInfoEdit"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));

// Signature pages
const SignatureDocumentList = lazy(
  () => import("@/components/signature/admin/DocumentList")
);
const SignatureCreateDocument = lazy(
  () => import("@/components/signature/admin/CreateDocument")
);
const SignatureDocumentSetup = lazy(
  () => import("@/components/signature/admin/DocumentSetup")
);
const SigningPage = lazy(
  () => import("@/components/signature/signer/SigningPage")
);
const SignatureTest = lazy(() => import("@/pages/test/SignatureTest"));
const ViewSignedDocument = lazy(
  () => import("@/components/signature/admin/ViewSignedDocument")
);

const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/auth/VerifyEmail"));
const TestResetPassword = lazy(() => import("@/pages/auth/TestResetPassword"));

const NotFound = lazy(() => import("@/pages/NotFound"));
const DatabaseError = lazy(() => import("@/pages/DatabaseError"));
const TestPage = lazy(() => import("@/pages/TestPage"));
const NetworkGridTestPage = lazy(() => import("@/pages/NetworkGridTestPage"));
const EnvDebug = lazy(() => import("@/pages/EnvDebug"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const FlipbookDemo = lazy(() => import("@/pages/FlipbookDemo"));
const AppleStylePage = lazy(() => import("@/pages/AppleStylePage"));
const AppleStyleTest = lazy(() => import("@/pages/AppleStyleTest"));
const AppleWidgetsDashboard = lazy(
  () => import("@/pages/AppleWidgetsDashboard")
);
const Amazing = lazy(() => import("@/pages/amazing"));
const AdminWidgetDashboard = lazy(
  () => import("@/pages/admin/WidgetDashboard")
);
const AdminCertificateSigners = lazy(
  () => import("@/pages/admin/CertificateSigners")
);
const GlobalLayoutDemo = lazy(() => import("@/pages/GlobalLayoutDemo"));
const TableDemo = lazy(() => import("@/pages/TableDemo"));
const PowerPointDemo = lazy(() => import("@/pages/PowerPointDemo"));
const FlipbookExample = lazy(() => import("@/pages/flipbook-example"));
const EnhancedFlipbookDemo = lazy(() => import("@/pages/EnhancedFlipbookDemo"));
const SimplePDFTest = lazy(() => import("@/pages/SimplePDFTest"));
const PDFFlipbookDemo = lazy(() => import("@/pages/PDFFlipbookDemo"));
const BookStylePDFDemo = lazy(() => import("@/pages/BookStylePDFDemo"));
const PDFViewersIndex = lazy(() => import("@/pages/PDFViewersIndex"));
const BasicPDFView = lazy(() => import("@/pages/PDFFlipbookDemo"));
// const CourseSectionTester = lazy(() => import("@/pages/CourseSectionTester"));
const Tutorial = lazy(() => import("@/pages/tutorial"));
const DevTutorial = lazy(() => import("@/pages/dev-tutorial"));
const AlertDemo = lazy(() => import("@/pages/AlertDemo"));
const PDFGeneratorTest = lazy(() => import("@/pages/PDFGeneratorTest"));
const TestPDFPage = lazy(() => import("@/pages/TestPDFPage"));
const SignCertificate = lazy(() => import("@/pages/SignCertificate"));

// Define routes
const router = createBrowserRouter([
  // Public routes - placed outside GlobalLayout
  {
    path: "sign-certificate",
    element: (
      <Suspense fallback={<Loading />}>
        <SignCertificate />
      </Suspense>
    ),
  },
  {
    path: "apple-style-test",
    element: (
      <Suspense fallback={<Loading />}>
        <AppleStyleTest />
      </Suspense>
    ),
  },
  {
    path: "apple-style",
    element: (
      <Suspense fallback={<Loading />}>
        <AppleStylePage />
      </Suspense>
    ),
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    errorElement: <DatabaseError />,
    children: [
      {
        index: true,
        element: <Navigate to="/auth/login" replace />,
      },
      {
        path: "login",
        element: (
          <Suspense fallback={<Loading />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: "register",
        element: (
          <Suspense fallback={<Loading />}>
            <Register />
          </Suspense>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <Suspense fallback={<Loading />}>
            <ForgotPassword />
          </Suspense>
        ),
      },
      {
        path: "verify-email",
        element: (
          <Suspense fallback={<Loading />}>
            <VerifyEmail />
          </Suspense>
        ),
      },
      {
        path: "reset-password",
        element: (
          <Suspense fallback={<Loading />}>
            <ResetPassword />
          </Suspense>
        ),
      },
      {
        path: "test-reset",
        element: (
          <Suspense fallback={<Loading />}>
            <TestResetPassword />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/",
    element: <GlobalLayout />,
    errorElement: <DatabaseError />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      // Main routes
      {
        path: "courses",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loading />}>
                <Courses />
              </Suspense>
            ),
          },
          {
            path: ":id",
            element: (
              <Suspense fallback={<Loading />}>
                <CourseDetails />
              </Suspense>
            ),
          },
        ],
      },
      // User routes
      {
        path: "user",
        element: (
          <ProtectedRoute requiredRole="user">
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/user/dashboard" replace />,
          },
          {
            path: "dashboard",
            element: (
              <Suspense fallback={<Loading />}>
                <UserDashboard />
              </Suspense>
            ),
          },
          {
            path: "courses",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    <UserCourses />
                  </Suspense>
                ),
              },
              {
                path: ":courseId",
                element: (
                  <Suspense fallback={<Loading />}>
                    <UserCourseDetail />
                  </Suspense>
                ),
              },
              {
                path: ":courseId/lessons/:lessonId",
                element: (
                  <Navigate
                    to={`/lesson${
                      window.location.pathname.split("/user/courses")[1]
                    }`}
                    replace
                  />
                ),
              },
            ],
          },
          {
            path: "profile",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    <UserProfile />
                  </Suspense>
                ),
              },
              {
                path: "edit",
                element: (
                  <Suspense fallback={<Loading />}>
                    <UserProfileEdit />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "certificates",
            element: (
              <Suspense fallback={<Loading />}>
                <UserCertificates />
              </Suspense>
            ),
          },
          {
            path: "hospital-info",
            element: (
              <Suspense fallback={<Loading />}>
                <HospitalInfoPage />
              </Suspense>
            ),
          },
          {
            path: "notifications",
            element: (
              <Suspense fallback={<Loading />}>
                <UserNotifications />
              </Suspense>
            ),
          },
          {
            path: "homepage",
            element: (
              <Suspense fallback={<Loading />}>
                <UserAppleStyleIcons />
              </Suspense>
            ),
          },
          // Messaging routes disabled
          // {
          //   path: "messaging",
          //   element: (
          //     <Suspense fallback={<Loading />}>
          //       <UserMessaging />
          //     </Suspense>
          //   ),
          // },
          // {
          //   path: "user-messaging",
          //   element: (
          //     <Suspense fallback={<Loading />}>
          //       <UserMessaging />
          //     </Suspense>
          //   ),
          // },
        ],
      },
      // Admin routes
      {
        path: "admin",
        element: (
          <ProtectedRoute requiredRole="admin">
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/admin/home" replace />,
          },
          {
            path: "home",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminHomePage />
              </Suspense>
            ),
          },
          {
            path: "dashboard",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminDashboard />
              </Suspense>
            ),
          },
          {
            path: "tables",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminTables />
              </Suspense>
            ),
          },
          {
            path: "widgets",
            element: <Navigate to="/admin-widgets" replace />,
          },
          {
            path: "users",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminUserManagement />
              </Suspense>
            ),
          },
          {
            path: "auth",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminAuth />
              </Suspense>
            ),
          },
          {
            path: "auth-requests",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminAuthRequests />
              </Suspense>
            ),
          },
          {
            path: "departments",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminDepartments />
              </Suspense>
            ),
          },
          {
            path: "sections",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdminSections />
                  </Suspense>
                ),
              },
              {
                path: ":sectionId/lessons",
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdminSectionLessons />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "translations",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminTranslations />
              </Suspense>
            ),
          },
          {
            path: "complaints",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminComplaints />
              </Suspense>
            ),
          },
          {
            path: "media-library",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminMediaLibrary />
              </Suspense>
            ),
          },
          {
            path: "profilePreview",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminProfilePreview />
              </Suspense>
            ),
          },
          {
            path: "profileEdit",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminProfileEdit />
              </Suspense>
            ),
          },
          {
            path: "hospital-info-edit",
            element: (
              <Suspense fallback={<Loading />}>
                <HospitalInfoEdit />
              </Suspense>
            ),
          },
          {
            path: "notifications",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminNotifications />
              </Suspense>
            ),
          },
          {
            path: "certificate-signers",
            element: (
              <Suspense fallback={<Loading />}>
                <AdminCertificateSigners />
              </Suspense>
            ),
          },
          // Admin messaging route disabled
          // {
          //   path: "messaging",
          //   element: (
          //     <Suspense fallback={<Loading />}>
          //       <AdminMessaging />
          //     </Suspense>
          //   ),
          // },
          // PDF Signature routes
          {
            path: "signature",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    <SignatureDocumentList />
                  </Suspense>
                ),
              },
              {
                path: "new",
                element: (
                  <Suspense fallback={<Loading />}>
                    <SignatureCreateDocument />
                  </Suspense>
                ),
              },
              {
                path: ":id/setup",
                element: (
                  <Suspense fallback={<Loading />}>
                    <SignatureDocumentSetup />
                  </Suspense>
                ),
              },
              {
                path: ":id/view",
                element: (
                  <Suspense fallback={<Loading />}>
                    <ViewSignedDocument />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "courses",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdminCourses />
                  </Suspense>
                ),
              },
              {
                path: "new",
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdminNewCourse />
                  </Suspense>
                ),
              },
              {
                path: "details/:courseId",
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdminCourseDetails />
                  </Suspense>
                ),
              },
              {
                path: "edit/:courseId",
                element: (
                  <Suspense fallback={<Loading />}>
                    <EditCourse />
                  </Suspense>
                ),
              },
              {
                path: ":id/sections",
                element: (
                  <Suspense fallback={<Loading />}>
                    <AdminCourseSections />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
      // Custom layout for lesson pages with nested routes

      {
        path: "enrolled-course/:courseId",
        element: (
          <ErrorBoundary
            fallback={
              <div className="p-8 bg-red-600 text-white">
                خطأ في صفحة الدورة!
              </div>
            }
          >
            <Suspense fallback={<Loading />}>
              <EnrolledCourseView />
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: "lesson",
        element: (
          <ErrorBoundary
            fallback={
              <div className="min-h-screen bg-black text-white p-8 flex items-center justify-center">
                <div className="max-w-2xl mx-auto text-center">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold mb-4">
                    Error Loading Lesson
                  </h1>
                  <p className="text-xl text-white/70 mb-8">
                    An unexpected error occurred while loading the lesson.
                    Please try refreshing the page or return to your courses.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="/user/courses"
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-colors inline-flex items-center hover:opacity-90"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
                        />
                      </svg>
                      Return to Courses
                    </a>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors inline-flex items-center"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Refresh Page
                    </button>
                  </div>
                </div>
              </div>
            }
          >
            <Suspense fallback={<Loading />}>
              <LessonLayout />
            </Suspense>
          </ErrorBoundary>
        ),
        children: [
          {
            path: ":courseId/lessons/:lessonId",
            element: (
              <Suspense fallback={<Loading />}>
                <ErrorBoundary
                  fallback={
                    <div className="min-h-screen bg-black text-white p-8 flex items-center justify-center">
                      <div className="max-w-2xl mx-auto text-center">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                          <svg
                            className="w-10 h-10 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <h1 className="text-3xl font-bold mb-4">
                          Error in Lesson Component
                        </h1>
                        <p className="text-xl text-white/70 mb-8">
                          An error occurred while rendering the lesson. Please
                          try refreshing the page.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                          <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg transition-colors hover:opacity-90"
                          >
                            Refresh Page
                          </button>
                          <a
                            href="/user/courses"
                            className="px-6 py-3 bg-white/10 text-white rounded-lg transition-colors hover:bg-white/20"
                          >
                            Back to Courses
                          </a>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <CourseLesson />
                </ErrorBoundary>
              </Suspense>
            ),
          },
          // Catch-all route for lesson layout
          {
            path: "*",
            element: (
              <Suspense fallback={<Loading />}>
                <Navigate to="/error/not-found" replace />
              </Suspense>
            ),
          },
        ],
      },

      // Standalone routes
      {
        path: "user-widgets",
        element: (
          <ProtectedRoute requiredRole="user">
            <Suspense fallback={<Loading />}>
              <UserWidgetsDashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "user/widgets",
        element: <Navigate to="/user-widgets" replace />,
      },
      {
        path: "privacy-policy",
        element: (
          <Suspense fallback={<Loading />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: "terms",
        element: (
          <Suspense fallback={<Loading />}>
            <Terms />
          </Suspense>
        ),
      },
      // PDF Signature public route
      {
        path: "sign/:token",
        element: (
          <Suspense fallback={<Loading />}>
            <SigningPage />
          </Suspense>
        ),
      },
      // Signature test page
      {
        path: "test/signature",
        element: (
          <Suspense fallback={<Loading />}>
            <SignatureTest />
          </Suspense>
        ),
      },

      {
        path: "apple-widgets",
        element: (
          <Suspense fallback={<Loading />}>
            <AppleWidgetsDashboard />
          </Suspense>
        ),
      },
      {
        path: "homePage",
        element: <Navigate to="/user/homepage" replace />,
      },
      {
        path: "tutorial",
        element: (
          <Suspense fallback={<Loading />}>
            <Tutorial />
          </Suspense>
        ),
      },
      {
        path: "dev-tutorial",
        element: (
          <Suspense fallback={<Loading />}>
            <DevTutorial />
          </Suspense>
        ),
      },
      {
        path: "amazing",
        element: (
          <Suspense fallback={<Loading />}>
            <Amazing />
          </Suspense>
        ),
      },
      {
        path: "flipbook-demo",
        element: (
          <Suspense fallback={<Loading />}>
            <FlipbookDemo />
          </Suspense>
        ),
      },
      {
        path: "powerpoint-demo",
        element: (
          <Suspense fallback={<Loading />}>
            <PowerPointDemo />
          </Suspense>
        ),
      },
      {
        path: "flipbook-example",
        element: (
          <Suspense fallback={<Loading />}>
            <FlipbookExample />
          </Suspense>
        ),
      },
      {
        path: "enhanced-flipbook",
        element: (
          <Suspense fallback={<Loading />}>
            <EnhancedFlipbookDemo />
          </Suspense>
        ),
      },
      {
        path: "simple-pdf",
        element: (
          <Suspense fallback={<Loading />}>
            <SimplePDFTest />
          </Suspense>
        ),
      },
      {
        path: "pdf-flipbook",
        element: (
          <Suspense fallback={<Loading />}>
            <PDFFlipbookDemo />
          </Suspense>
        ),
      },
      {
        path: "book-pdf",
        element: (
          <Suspense fallback={<Loading />}>
            <BookStylePDFDemo />
          </Suspense>
        ),
      },
      {
        path: "pdf-viewers",
        element: (
          <Suspense fallback={<Loading />}>
            <PDFViewersIndex />
          </Suspense>
        ),
      },
      {
        path: "basic-pdf",
        element: (
          <Suspense fallback={<Loading />}>
            <BasicPDFView />
          </Suspense>
        ),
      },
      {
        path: "admin-widgets",
        element: (
          <ProtectedRoute requiredRole="admin">
            <Suspense fallback={<Loading />}>
              <AdminWidgetDashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/widgets",
        element: <Navigate to="/admin-widgets" replace />,
      },
      // Test routes
      {
        path: "test",
        element: (
          <Suspense fallback={<Loading />}>
            <TestPage />
          </Suspense>
        ),
      },
      {
        path: "api-test",
        element: (
          <Suspense fallback={<Loading />}>
            <ApiTestPage />
          </Suspense>
        ),
      },
      {
        path: "network-grid-test",
        element: (
          <Suspense fallback={<Loading />}>
            <NetworkGridTestPage />
          </Suspense>
        ),
      },
      {
        path: "global-demo",
        element: (
          <Suspense fallback={<Loading />}>
            <GlobalLayoutDemo />
          </Suspense>
        ),
      },
      {
        path: "table-demo",
        element: (
          <Suspense fallback={<Loading />}>
            <TableDemo />
          </Suspense>
        ),
      },
      {
        path: "pdf-generator-test",
        element: (
          <Suspense fallback={<Loading />}>
            <PDFGeneratorTest />
          </Suspense>
        ),
      },
      {
        path: "env-debug",
        element: (
          <Suspense fallback={<Loading />}>
            <EnvDebug />
          </Suspense>
        ),
      },
      {
        path: "alert-demo",
        element: (
          <Suspense fallback={<Loading />}>
            <AlertDemo />
          </Suspense>
        ),
      },
      {
        path: "test-pdf",
        element: (
          <Suspense fallback={<Loading />}>
            <TestPDFPage />
          </Suspense>
        ),
      },
      // {
      //   path: "course-section-tester",
      //   element: (
      //     <Suspense fallback={<Loading />}>
      //       <CourseSectionTester />
      //     </Suspense>
      //   ),
      // },
      // {
      //   path: "claude-test",
      //   element: (
      //     <Suspense fallback={<Loading />}>
      //       <ClaudeTest />
      //     </Suspense>
      //   ),
      // },
      // MCP servers have been completely removed
      // Redirect /login to /auth/login for better user experience
      {
        path: "login",
        element: <Navigate to="/auth/login" replace />,
      },
      // Redirect /profilepreview to /admin/profilePreview for better URL handling
      {
        path: "profilepreview",
        element: <Navigate to="/admin/profilePreview" replace />,
      },
      {
        path: "*",
        element: (
          <Suspense fallback={<Loading />}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
]);

export default function Router() {
  return <RouterProvider router={router} />;
}
